import React, { useState } from 'react';
import axios from 'axios';
import { Search, TrendingDown, Upload, Phone, Euro, Wifi, Smartphone, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OPERATORS = ['TIM', 'Vodafone', 'WindTre', 'Iliad', 'Fastweb', 'ho. Mobile', 'PosteMobile', 'CoopVoce', 'Kena Mobile', 'Altro'];

const ComparatorPage = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [results, setResults] = useState(null);
    const [form, setForm] = useState({
        phone_number: '',
        current_operator: '',
        monthly_cost: '',
        bill_type: '',
        customer_name: '',
        customer_email: '',
    });
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadedFileId, setUploadedFileId] = useState(null);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios.post(`${API}/api/telecom-offers/upload-bill`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setUploadedFile(file.name);
            setUploadedFileId(response.data.id);
            toast.success('Bolletta caricata!');
        } catch {
            toast.error('Errore nel caricamento');
        } finally {
            setUploading(false);
        }
    };

    const handleCompare = async () => {
        if (!form.current_operator || !form.monthly_cost || !form.bill_type) {
            toast.error('Compila operatore, costo mensile e tipo');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`${API}/api/telecom-offers/compare`, {
                ...form,
                monthly_cost: parseFloat(form.monthly_cost),
                bill_file_id: uploadedFileId
            });
            setResults(response.data);
            setStep(3);
        } catch {
            toast.error('Errore nel confronto');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                        <span className="text-primary">Confronta</span> la tua Bolletta
                    </h1>
                    <p className="text-muted max-w-2xl mx-auto">
                        Scopri quanto puoi risparmiare! Inserisci i dati della tua bolletta e ti mostreremo le offerte migliori.
                    </p>
                </div>

                {/* Progress */}
                <div className="flex items-center justify-center mb-8">
                    {[1, 2, 3].map((s) => (
                        <React.Fragment key={s}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${step >= s ? 'bg-primary text-white' : 'bg-surface border border-border text-muted'}`}>
                                {step > s ? <Check className="w-5 h-5" /> : s}
                            </div>
                            {s < 3 && <div className={`w-20 h-1 mx-2 rounded ${step > s ? 'bg-primary' : 'bg-border'}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step 1: Bill Info */}
                {step === 1 && (
                    <div className="bg-surface border border-border rounded-xl p-6 animate-fade-in">
                        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                            <Euro className="w-5 h-5 text-primary" />
                            Dati della tua bolletta
                        </h2>
                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Tipo di offerta *</Label>
                                    <Select value={form.bill_type} onValueChange={(v) => setForm({...form, bill_type: v})}>
                                        <SelectTrigger className="bg-surface-elevated border-border mt-1" data-testid="bill-type">
                                            <SelectValue placeholder="Seleziona..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface border-border">
                                            <SelectItem value="mobile"><Smartphone className="w-4 h-4 inline mr-2" />Mobile</SelectItem>
                                            <SelectItem value="fibra"><Wifi className="w-4 h-4 inline mr-2" />Fibra Casa</SelectItem>
                                            <SelectItem value="mobile_fibra">Mobile + Fibra</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Operatore attuale *</Label>
                                    <Select value={form.current_operator} onValueChange={(v) => setForm({...form, current_operator: v})}>
                                        <SelectTrigger className="bg-surface-elevated border-border mt-1" data-testid="current-operator">
                                            <SelectValue placeholder="Seleziona..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface border-border">
                                            {OPERATORS.map(op => (
                                                <SelectItem key={op} value={op}>{op}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label>Quanto paghi al mese? (€) *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.monthly_cost}
                                    onChange={(e) => setForm({...form, monthly_cost: e.target.value})}
                                    placeholder="es. 29.99"
                                    className="bg-surface-elevated border-border mt-1"
                                    data-testid="monthly-cost"
                                />
                            </div>
                            <div>
                                <Label>Carica bolletta (opzionale)</Label>
                                <div className="mt-1 border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors" onClick={() => document.getElementById('bill-upload').click()}>
                                    <input id="bill-upload" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileUpload} />
                                    {uploading ? (
                                        <p className="text-primary animate-pulse">Caricamento...</p>
                                    ) : uploadedFile ? (
                                        <p className="text-success flex items-center justify-center gap-2"><Check className="w-4 h-4" /> {uploadedFile}</p>
                                    ) : (
                                        <p className="text-muted"><Upload className="w-5 h-5 inline mr-2" />PDF o foto della bolletta</p>
                                    )}
                                </div>
                            </div>
                            <Button className="w-full btn-primary" onClick={() => setStep(2)} disabled={!form.bill_type || !form.current_operator || !form.monthly_cost} data-testid="step1-next-compare">
                                Continua <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 2: Contact Info */}
                {step === 2 && (
                    <div className="bg-surface border border-border rounded-xl p-6 animate-fade-in">
                        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                            <Phone className="w-5 h-5 text-primary" />
                            I tuoi contatti (opzionale)
                        </h2>
                        <p className="text-muted text-sm mb-4">Lascia i tuoi dati per essere ricontattato con l'offerta personalizzata</p>
                        <div className="space-y-4">
                            <div>
                                <Label>Nome</Label>
                                <Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} placeholder="Mario Rossi" className="bg-surface-elevated border-border mt-1" data-testid="compare-name" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} placeholder="nome@email.com" className="bg-surface-elevated border-border mt-1" data-testid="compare-email" />
                            </div>
                            <div>
                                <Label>Telefono</Label>
                                <Input value={form.phone_number} onChange={(e) => setForm({...form, phone_number: e.target.value})} placeholder="+39 333 1234567" className="bg-surface-elevated border-border mt-1" data-testid="compare-phone" />
                            </div>
                            <div className="flex gap-4">
                                <Button variant="outline" className="flex-1 border-border" onClick={() => setStep(1)}>Indietro</Button>
                                <Button className="flex-1 btn-primary" onClick={handleCompare} disabled={loading} data-testid="compare-btn">
                                    {loading ? 'Confronto...' : <><Search className="w-4 h-4 mr-2" /> Confronta offerte</>}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Results */}
                {step === 3 && results && (
                    <div className="animate-fade-in">
                        <div className="bg-surface border border-primary/30 rounded-2xl p-8 text-center">
                            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success" />
                            </div>
                            <h2 className="text-2xl font-bold mb-3">Richiesta ricevuta!</h2>
                            <p className="text-muted text-lg mb-6">
                                Sarai ricontattato presto per la miglior offerta disponibile in base alle tue esigenze.
                            </p>
                            <div className="bg-surface-elevated rounded-xl p-4 max-w-md mx-auto mb-6 text-left space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-muted">Operatore attuale:</span>
                                    <span className="font-medium">{form.current_operator}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted">Costo mensile:</span>
                                    <span className="font-medium text-warning">€{parseFloat(form.monthly_cost).toFixed(2)}/mese</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted">Tipo:</span>
                                    <span className="font-medium">{form.bill_type === 'mobile' ? 'Mobile' : form.bill_type === 'fibra' ? 'Fibra' : 'Mobile+Fibra'}</span>
                                </div>
                            </div>
                            <p className="text-sm text-muted mb-6">
                                Un nostro consulente analizzerà la tua bolletta e ti proporrà l'offerta più vantaggiosa.
                                <br />Per info: <a href="tel:0819228487" className="text-primary hover:underline">081 922 8487</a>
                            </p>
                            <Button className="btn-primary px-8" onClick={() => { setStep(1); setResults(null); setForm({phone_number:'',current_operator:'',monthly_cost:'',bill_type:'',customer_name:'',customer_email:''}); setUploadedFile(null); setUploadedFileId(null); }} data-testid="compare-again">
                                Confronta un'altra bolletta
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComparatorPage;
